import { ApplicationRef, Injectable } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { Observable, firstValueFrom, from, of, race, timer } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';

import { isModuleLoadFailure } from './module-load-failure.util';

/**
 * Preloading strategy for Spotlite's ~30 lazy routes.
 *
 * Replaces `PreloadAllModules`, which fired every lazy `import()` at once the
 * moment the first route resolved. On a guest phone that meant ~30 parallel chunk
 * fetches over cellular, racing the chunk the guest is actually waiting for, right
 * when they had just tapped a link from an SMS or email. Whichever ones lost that
 * race rejected with "Importing a module script failed." (Sentry SPOTLITE-C8) — and
 * because a failed dynamic import is cached permanently in the document's module
 * map, those routes then stayed broken for the rest of the visit even after the
 * network recovered.
 *
 * This strategy keeps the navigation benefit of preloading while removing the
 * conditions that produced the failures:
 *  - waits for the app to settle so preloads never compete with the initial route,
 *  - preloads one route at a time, spaced out, instead of all at once,
 *  - skips entirely when the device is offline or asking us to conserve data,
 *  - never lets a preload failure reach the Angular `ErrorHandler`, since a preload
 *    is an optimisation and its failure is not something the guest experienced.
 */

/** Give up waiting for app stability after this long and start preloading anyway. */
export const STABILITY_TIMEOUT_MS = 5000;

/** Idle gap between consecutive preloads, so they stay off the critical path. */
export const PRELOAD_SPACING_MS = 300;

@Injectable({ providedIn: 'root' })
export class SpotlitePreloadStrategy implements PreloadingStrategy {
  /**
   * Router paths whose chunk failed to preload. Once a path is in here its module
   * map entry is poisoned, so a later navigation to it cannot succeed in this
   * document — `ChunkLoadRecoveryService` uses that to reload without waiting for
   * a second failure.
   */
  private readonly failedPaths = new Set<string>();

  /** Tail of the serial preload chain; each route appends itself to it. */
  private tail: Promise<unknown>;

  constructor(private readonly appRef: ApplicationRef) {
    this.tail = this.waitForAppToSettle();
  }

  preload(route: Route, load: () => Observable<unknown>): Observable<unknown> {
    if (!this.canPreload()) {
      return of(null);
    }

    const task = this.tail
      .then(() => firstValueFrom(timer(PRELOAD_SPACING_MS)))
      // Re-check: the connection may have dropped while earlier routes preloaded.
      // `defaultValue` matters here: Angular's `load()` observable can complete
      // without emitting (e.g. a route skipped by a guard), and without it
      // `firstValueFrom` throws `EmptyError` for a case that isn't a failure at all.
      .then(() => (this.canPreload() ? firstValueFrom(load(), { defaultValue: null }) : null))
      .catch((error: unknown) => {
        this.onPreloadFailed(route, error);
        return null;
      });

    // Chain the next route behind this one regardless of outcome.
    this.tail = task;

    return from(task);
  }

  /**
   * True when a preload covering `urlPath` previously failed and poisoned its
   * module map.
   *
   * Compared segment-wise rather than by string equality, because the recorded
   * value is a route *pattern* and the argument is a real navigation URL. A
   * recorded route matches when its segments are a prefix of the URL's — a lazy
   * module owns every route beneath its own path — and a `:param` segment matches
   * any single segment, so `survey/:token` matches `/survey/abc123`.
   */
  hasFailedPath(urlPath: string): boolean {
    const urlSegments = toSegments(urlPath);
    if (urlSegments.length === 0) {
      return false;
    }

    for (const failedPath of this.failedPaths) {
      const routeSegments = toSegments(failedPath);
      if (routeSegments.length === 0 || routeSegments.length > urlSegments.length) {
        continue;
      }

      const matches = routeSegments.every(
        (segment, index) => segment.startsWith(':') || segment === urlSegments[index]
      );
      if (matches) {
        return true;
      }
    }

    return false;
  }

  private onPreloadFailed(route: Route, error: unknown): void {
    if (route.path && route.path !== '**') {
      this.failedPaths.add(route.path);
    }

    // A failed preload is expected on flaky mobile connections and the guest never
    // saw it, so it is logged rather than thrown. Anything else is a real bug in the
    // lazy module and is re-surfaced as an unhandled rejection for Sentry to catch.
    if (isModuleLoadFailure(error)) {
      console.warn('[Spotlite] preload skipped for route', route.path, error);
      return;
    }

    console.error('[Spotlite] preload of route failed unexpectedly', route.path, error);
    void Promise.reject(error);
  }

  private canPreload(): boolean {
    const navigator = globalThis.navigator;
    if (!navigator) {
      return false;
    }

    // `onLine === false` is a reliable negative in every browser; `true` is only a
    // hint, which is fine — a wrong guess here just costs one failed preload.
    if (navigator.onLine === false) {
      return false;
    }

    const connection = (navigator as NavigatorWithConnection).connection;
    if (!connection) {
      // Safari exposes no Network Information API. Preload, but serially — that is
      // what the spacing above is for.
      return true;
    }

    if (connection.saveData === true) {
      return false;
    }

    return !(connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g');
  }

  /**
   * Resolves once Angular reports stability, or after `STABILITY_TIMEOUT_MS`.
   *
   * The timeout matters: Spotlite holds a SignalR connection and polling timers
   * that can keep the zone busy indefinitely, and without a fallback the app would
   * silently stop preloading altogether.
   */
  private waitForAppToSettle(): Promise<unknown> {
    const stable$ = this.appRef.isStable.pipe(
      filter(stable => stable),
      take(1)
    );

    return firstValueFrom(race(stable$, timer(STABILITY_TIMEOUT_MS).pipe(map(() => true))));
  }
}

/** Splits a route pattern or URL path into non-empty segments. */
function toSegments(path: string): string[] {
  return path.split(/[?#]/, 1)[0].split('/').filter(Boolean);
}

/** Subset of the Network Information API that browsers actually agree on. */
interface NavigatorWithConnection extends Navigator {
  connection?: {
    saveData?: boolean;
    effectiveType?: string;
  };
}
